/**
 * Utility functions for working with hosted categories
 */

/**
 * Find a category by ID within the categories tree
 * @param {Array} categories - Array of category objects
 * @param {string} categoryId - ID to search for
 * @returns {Object|null} - Found category or null
 */
const findCategoryById = (categories, categoryId) => {
  if (!categories || !Array.isArray(categories) || !categoryId) return null;

  for (const category of categories) {
    if (category.id === categoryId) {
      return category;
    }

    // Search in subcategories recursively
    if (category.subcategories) {
      const found = findCategoryById(category.subcategories, categoryId);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Get category label by ID from hosted categories
 * @param {Array} categories - Array of hosted category objects
 * @param {string} categoryId - Category ID to look up
 * @returns {string|null} - Category label or null if not found
 */
export const getCategoryLabel = (categories, categoryId) => {
  const category = findCategoryById(categories, categoryId);
  return category ? category.name : null;
};

/**
 * Get category path labels (breadcrumb) for a listing's categories
 * @param {Array} categories - Array of hosted category objects
 * @param {Object} publicData - Listing's publicData containing categoryLevel1, categoryLevel2, etc.
 * @returns {Array} - Array of {id, label} objects for breadcrumb
 */
export const getCategoryBreadcrumbs = (categories, publicData) => {
  if (!categories || !publicData) return [];

  const breadcrumbs = [];
  const maxLevels = 4;
  let currentCategories = categories;

  for (let level = 1; level <= maxLevels; level++) {
    const categoryId = publicData[`categoryLevel${level}`];

    if (!categoryId || !currentCategories) break;

    const categoryLabel = getCategoryLabel(currentCategories, categoryId);
    if (categoryLabel) {
      breadcrumbs.push({ id: categoryId, label: categoryLabel });

      // Find subcategories for next level
      const category = findCategoryById(currentCategories, categoryId);
      currentCategories = category?.subcategories;
    } else {
      break;
    }
  }

  return breadcrumbs;
};

/**
 * Get search query params for category filtering
 * @param {string} categoryLevel - Which level (categoryLevel1, categoryLevel2, etc.)
 * @param {string} categoryId - Category ID value
 * @returns {string} - Search query string
 */
export const getCategorySearchParam = (categoryLevel, categoryId) => {
  if (!categoryLevel || !categoryId) return '';

  // Map hosted category levels to search param names (support up to 4 levels)
  const paramMap = {
    categoryLevel1: 'pub_categoryLevel1',
    categoryLevel2: 'pub_categoryLevel2',
    categoryLevel3: 'pub_categoryLevel3',
    categoryLevel4: 'pub_categoryLevel4',
  };

  const paramName = paramMap[categoryLevel];
  return paramName ? `${paramName}=${categoryId}` : '';
};

// Legacy conversion function - no longer needed since we're fully migrated to hosted categories
// Kept for reference only
// export const convertLegacyCategoriesToHosted = (oldPublicData) => {
//   if (!oldPublicData) return {};
//   const converted = { ...oldPublicData };
//   if (oldPublicData.category?.value) {
//     converted.categoryLevel1 = oldPublicData.category.value;
//     delete converted.category;
//   }
//   if (oldPublicData.subcategory?.value) {
//     converted.categoryLevel2 = oldPublicData.subcategory.value;
//     delete converted.subcategory;
//   }
//   if (oldPublicData.subsubcategory?.value) {
//     converted.categoryLevel3 = oldPublicData.subsubcategory.value;
//     delete converted.subsubcategory;
//   }
//   return converted;
// };
